/**
 * "Quick estimate" badge for results built from the Edward Jones Quick Plan.
 *
 * It reads the applied (latest) inference set and renders only when that row was produced by the
 * `edj-minimal-v1` model input (`isEdjQuickEstimate` in @shared). It never reads client metadata,
 * so a later full intake, which writes a standard row, removes the badge with no other change.
 *
 * Used on the predictions page and in the HTML reports. The React-PDF report cannot render DOM
 * components, so it renders its own text tag from the same predicate.
 */
import { useSelector } from 'react-redux';
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { isEdjQuickEstimate } from '@shared';
import { selectAppliedInferenceSet } from '@/model/selectors';

export const QUICK_ESTIMATE_LABEL = 'Quick estimate';
export const QUICK_ESTIMATE_DESCRIPTION =
  'Based on limited client information. Results may change when more complete information becomes available.';

interface QuickEstimateBadgeProps {
  className?: string;
  /** Printed reports have nothing to hover, so they show the badge alone. */
  withTooltip?: boolean;
}

export function QuickEstimateBadge({ className, withTooltip = true }: QuickEstimateBadgeProps) {
  const appliedInferenceSet = useSelector(selectAppliedInferenceSet);

  if (!isEdjQuickEstimate(appliedInferenceSet)) {
    return null;
  }

  const badge = (
    <Badge
      className={className}
      data-testid="quick-estimate-badge"
    >
      {QUICK_ESTIMATE_LABEL}
    </Badge>
  );

  if (!withTooltip) {
    return badge;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">{badge}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{QUICK_ESTIMATE_DESCRIPTION}</TooltipContent>
    </Tooltip>
  );
}
